import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTesterMyExceptions } from '../../../hooks/analytics/useTesterReports.ts';
import { WidgetCard, formatPercent } from '../controller/shared.tsx';
import type { TesterMyExceptionRow } from '../../../request/analytics/testerReports.ts';

const SEVERITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  critical: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-700',
};

const SeverityChip: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <span className="text-xs text-gray-400">Unrated</span>;
  const classes = SEVERITY_COLORS[value.toLowerCase()] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${classes}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
};

const ExceptionRow: React.FC<{ row: TesterMyExceptionRow; index: number }> = ({ row, index }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {row.control_id} {row.label ? `— ${row.label}` : ''}
            </p>
            {row.record_ref && <p className="text-xs text-gray-500 truncate">Sample: {row.record_ref}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SeverityChip value={row.severity} />
          {row.deviation_rate != null && (
            <span className="text-xs text-gray-500">{formatPercent(row.deviation_rate * 100, 1)}</span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100 bg-gray-50 text-sm space-y-1.5">
          <p>
            <span className="font-medium text-gray-600">Status:</span>{' '}
            <span className="text-gray-800">{row.status ?? '—'}</span>
          </p>
          {row.observation && (
            <p>
              <span className="font-medium text-gray-600">Observation:</span>{' '}
              <span className="text-gray-800">{row.observation}</span>
            </p>
          )}
          {row.findings_summary && (
            <p>
              <span className="font-medium text-gray-600">Findings:</span>{' '}
              <span className="text-gray-800">{row.findings_summary}</span>
            </p>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Report #33 — My Exception Log. Expandable exception list (server already
 * filters to the caller's assigned test runs), with a client-side severity
 * filter above the list.
 */
export const MyExceptionsList: React.FC<{ controlIdFilter?: Set<string> | null; assigneeUserId?: number | null }> = ({
  controlIdFilter,
  assigneeUserId,
}) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useTesterMyExceptions(assigneeUserId);
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  const severities = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r.severity) set.add(r.severity.toLowerCase());
    });
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    if (severityFilter === 'all') return rows;
    if (severityFilter === 'unrated') return rows.filter((r) => !r.severity);
    return rows.filter((r) => (r.severity ?? '').toLowerCase() === severityFilter);
  }, [rows, severityFilter]);

  return (
    <WidgetCard
      title="My Exceptions"
      description="Exceptions found across your assigned test runs"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load your exceptions.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No exceptions on your assigned controls."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      {severities.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {['all', ...severities, 'unrated'].map((sev) => (
            <button
              key={sev}
              type="button"
              onClick={() => setSeverityFilter(sev)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize border ${
                severityFilter === sev
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {sev}
            </button>
          ))}
        </div>
      )}
      <ul className="analytics-scroll space-y-2 max-h-96 overflow-y-auto">
        {filtered.map((row, idx) => (
          <ExceptionRow key={`${row.control_id}-${row.record_ref ?? idx}`} row={row} index={idx} />
        ))}
        {filtered.length === 0 && rows.length > 0 && (
          <li className="text-center text-sm text-gray-400 py-6">No exceptions match this severity.</li>
        )}
      </ul>
    </WidgetCard>
  );
};

export default MyExceptionsList;
